import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { emailValidationApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface ValidationResult {
  email: string;
  isValid: boolean;
  syntax: boolean;
  disposable: boolean;
  roleBased: boolean;
  mxValid: boolean;
  smtpValid: boolean | 'unknown';
  errors: string[];
  warnings: string[];
}

const EmailValidator: React.FC = () => {
  const [emailInput, setEmailInput] = useState('');
  const [results, setResults] = useState<ValidationResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const handleValidate = async () => {
    if (!emailInput.trim()) {
      setError('Please enter at least one email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Split emails by comma, newline, or space and clean them
      const emails = emailInput
        .split(/[,\n\s]+/)
        .map(email => email.trim())
        .filter(email => email.length > 0);

      if (emails.length === 0) {
        setError('Please enter valid email addresses');
        return;
      }

      // Use batch validation for multiple emails, single validation for one email
      let response;
      if (emails.length === 1) {
        response = await emailValidationApi.validateSingle(emails[0], user?.accountId);
        setResults([response.result]);
      } else {
        response = await emailValidationApi.validateBatch(emails, user?.accountId);
        setResults(response.results);
      }
    } catch (err: any) {
      console.error('Validation error:', err);
      // Don't show network errors to user, just show that validation failed
      setError('Validation service temporarily unavailable. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickValidate = async () => {
    if (!emailInput.trim()) {
      setError('Please enter at least one email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const emails = emailInput
        .split(/[,\n\s]+/)
        .map(email => email.trim())
        .filter(email => email.length > 0);

      if (emails.length === 0) {
        setError('Please enter valid email addresses');
        return;
      }

      const response = await emailValidationApi.quickValidate(emails, user?.accountId);
      setResults(response.results);
    } catch (err: any) {
      console.error('Quick validation error:', err);
      // Don't show network errors to user, just show that validation failed
      setError('Validation service temporarily unavailable. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setEmailInput('');
    setResults([]);
    setError('');
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getValidationIcon = (result: ValidationResult) => {
    if (result.isValid) {
      return <CheckCircleIcon color="success" />;
    } else {
      return <ErrorIcon color="error" />;
    }
  };

  const getValidationColor = (result: ValidationResult) => {
    if (result.isValid) return 'success';
    if (result.syntax && result.mxValid) return 'warning';
    return 'error';
  };

  const getValidationSummary = (result: ValidationResult) => {
    if (result.isValid) {
      if (result.smtpValid === 'unknown') return 'Valid (SMTP unknown)';
      return 'Valid';
    }
    if (!result.syntax) return 'Invalid syntax';
    if (result.disposable) return 'Disposable domain';
    if (result.roleBased) return 'Role-based address';
    if (!result.mxValid) return 'No MX records';
    if (result.smtpValid === false) return 'Mailbox not found';
    if (result.smtpValid === 'unknown') return 'SMTP verification blocked';
    return 'Invalid';
  };

  const validEmails = results.filter(r => r.isValid);
  const invalidEmails = results.filter(r => !r.isValid);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Fixed Header */}
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
        pb: 2,
        mb: 3
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Email Validator
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Validate email addresses for syntax, deliverability, and quality before sending campaigns
        </Typography>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
        
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Email Addresses"
            placeholder="Enter email addresses (separated by commas, spaces, or new lines)"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            variant="outlined"
            size="small"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleValidate}
            disabled={loading || !emailInput.trim()}
            size="small"
          >
            {loading ? <CircularProgress size={20} /> : 'Full Validation'}
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleQuickValidate}
            disabled={loading || !emailInput.trim()}
            size="small"
          >
            Quick Check
          </Button>
          
          <IconButton
            onClick={handleClear}
            disabled={loading}
            size="small"
          >
            <ClearIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {results.length > 0 && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip
                label={`${validEmails.length} Valid`}
                color="success"
                size="small"
              />
              <Chip
                label={`${invalidEmails.length} Invalid`}
                color="error"
                size="small"
              />
            </Box>

            <List dense>
              {results.map((result, index) => (
                <React.Fragment key={index}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {getValidationIcon(result)}
                        </ListItemIcon>
                        <ListItemText
                          primary={result.email}
                          secondary={getValidationSummary(result)}
                        />
                        <Chip
                          label={result.isValid ? 'Valid' : 'Invalid'}
                          color={getValidationColor(result) as any}
                          size="small"
                        />
                        <Tooltip title="Copy email">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(result.email);
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Validation Details:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          <Chip
                            label="Syntax"
                            color={result.syntax ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label="MX Records"
                            color={result.mxValid ? 'success' : 'error'}
                            size="small"
                            variant="outlined"
                          />
                          {result.smtpValid !== undefined && (
                            <Chip
                              label={result.smtpValid === 'unknown' ? 'SMTP Unknown' : 'SMTP Check'}
                              color={result.smtpValid === true ? 'success' : result.smtpValid === 'unknown' ? 'warning' : 'error'}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          <Chip
                            label={result.disposable ? 'Disposable' : 'Not Disposable'}
                            color={result.disposable ? 'warning' : 'success'}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            label={result.roleBased ? 'Role-based' : 'Personal'}
                            color={result.roleBased ? 'warning' : 'success'}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        
                        {result.warnings && result.warnings.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="warning.main" gutterBottom>
                              Warnings:
                            </Typography>
                            <List dense>
                              {result.warnings.map((warning, warningIndex) => (
                                <ListItem key={warningIndex} sx={{ py: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 30 }}>
                                    <WarningIcon color="warning" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={warning}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}

                        {result.errors.length > 0 && (
                          <Box>
                            <Typography variant="subtitle2" color="error" gutterBottom>
                              Issues Found:
                            </Typography>
                            <List dense>
                              {result.errors.map((error, errorIndex) => (
                                <ListItem key={errorIndex} sx={{ py: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 30 }}>
                                    <WarningIcon color="error" fontSize="small" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={error}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  {index < results.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default EmailValidator;
